import { useState } from "react";
import { Avatar } from "../components/ui/Avatar";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { SearchInput, Select } from "../components/ui/Fields";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { StepUpDialog } from "../components/ui/StepUpDialog";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import { useNavigate } from "react-router-dom";
import type { Profile } from "../api/types";

type PendingAction =
  | { kind: "ban" | "unban"; userId: string; username: string }
  | { kind: "verify" | "unverify"; userId: string; username: string }
  | { kind: "bulk-ban" | "bulk-unban"; userIds: string[] };

export function UsersPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | "banned" | "verified">("");
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<Profile>("/admin/users", {
    search: search || undefined,
    status: statusFilter || undefined,
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();

  function toggleSelected(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))));
  }

  async function runVerifyToggle(action: Extract<PendingAction, { kind: "verify" | "unverify" }>) {
    setIsSubmitting(true);
    try {
      await apiRequest(`/admin/users/${action.userId}/verify`, { method: "POST", body: { verified: action.kind === "verify" } });
      showToast(`${action.username} updated`, "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not complete that action", "error");
    } finally {
      setIsSubmitting(false);
      setPending(null);
    }
  }

  async function runStepUpAction(stepUpToken: string) {
    if (!pending) return;
    setIsSubmitting(true);
    try {
      if (pending.kind === "ban" || pending.kind === "unban") {
        await apiRequest(`/admin/users/${pending.userId}/ban`, { method: "POST", body: { banned: pending.kind === "ban", stepUpToken } });
        showToast(`${pending.username} ${pending.kind === "ban" ? "banned" : "unbanned"}`, "success");
      } else if (pending.kind === "bulk-ban" || pending.kind === "bulk-unban") {
        await apiRequest("/admin/users/bulk-ban", {
          method: "POST",
          body: { userIds: pending.userIds, banned: pending.kind === "bulk-ban", stepUpToken },
        });
        showToast(`${pending.userIds.length} users updated`, "success");
        setSelected(new Set());
      }
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not complete that action", "error");
    } finally {
      setIsSubmitting(false);
      setPending(null);
    }
  }

  const columns: Column<Profile>[] = [
    {
      header: "",
      className: "w-10",
      render: (row) => (
        <input
          type="checkbox"
          checked={selected.has(row.id)}
          onChange={() => toggleSelected(row.id)}
          onClick={(e) => e.stopPropagation()}
          className="h-4 w-4 rounded border-surface-border"
        />
      ),
    },
    {
      header: "User",
      render: (row) => (
        <div className="flex items-center gap-3">
          <Avatar src={row.avatar_url} className="h-8 w-8 rounded-full object-cover" />
          {/* display_name removed project-wide (round 12) — username is
              the one canonical public identity. */}
          <p className="font-medium text-ink">{row.username}</p>
        </div>
      ),
    },
    { header: "User ID", render: (row) => <span className="font-mono text-xs">{row.user_id}</span> },
    { header: "Country", render: (row) => row.country ?? "—" },
    {
      header: "Status",
      render: (row) => (
        <div className="flex gap-1.5">
          {row.is_verified && <Badge tone="gold">Verified</Badge>}
          {row.is_banned && <Badge tone="danger">Banned</Badge>}
          {!row.is_verified && !row.is_banned && <Badge tone="neutral">Standard</Badge>}
        </div>
      ),
    },
    { header: "Joined", render: (row) => new Date(row.created_at).toLocaleDateString() },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => navigate(`/users/${row.id}`)}>
            View
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => runVerifyToggle({ kind: row.is_verified ? "unverify" : "verify", userId: row.id, username: row.username })}
          >
            {row.is_verified ? "Unverify" : "Verify"}
          </Button>
          <Button
            size="sm"
            variant={row.is_banned ? "secondary" : "danger"}
            onClick={() => setPending({ kind: row.is_banned ? "unban" : "ban", userId: row.id, username: row.username })}
          >
            {row.is_banned ? "Unban" : "Ban"}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Users">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Search by username..." />
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)} className="w-auto">
            <option value="">All statuses</option>
            <option value="verified">Verified only</option>
            <option value="banned">Banned only</option>
          </Select>
        </div>

        {selected.size > 0 && (
          <div className="flex items-center gap-2 rounded-lg border border-surface-border bg-surface-card px-3 py-2">
            <span className="text-xs text-ink-muted">{selected.size} selected</span>
            <Button size="sm" variant="danger" onClick={() => setPending({ kind: "bulk-ban", userIds: Array.from(selected) })}>
              Bulk Ban
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setPending({ kind: "bulk-unban", userIds: Array.from(selected) })}>
              Bulk Unban
            </Button>
          </div>
        )}
      </div>

      <div className="mb-2 flex items-center gap-2 px-1">
        <input type="checkbox" checked={rows.length > 0 && selected.size === rows.length} onChange={toggleSelectAll} className="h-4 w-4 rounded border-surface-border" />
        <span className="text-xs text-ink-faint">Select all on this page</span>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No users found"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <StepUpDialog
        isOpen={!!pending && (pending.kind === "ban" || pending.kind === "unban" || pending.kind === "bulk-ban" || pending.kind === "bulk-unban")}
        onClose={() => setPending(null)}
        onVerified={runStepUpAction}
        title={
          pending && "userIds" in pending
            ? `${pending.kind === "bulk-ban" ? "Ban" : "Unban"} ${pending.userIds.length} users?`
            : pending && "username" in pending
            ? `${pending.kind === "ban" ? "Ban" : "Unban"} ${pending.username}?`
            : ""
        }
        description={
          pending?.kind === "ban" || pending?.kind === "bulk-ban"
            ? "This immediately blocks the affected account(s) from using the app. Re-enter your password to confirm."
            : "Re-enter your password to confirm this action."
        }
      />
    </AppShell>
  );
}
